import { PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMonths, format, isSameMonth, isToday, isWeekend, startOfMonth } from 'date-fns';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@utils/cn';
import { longMonth } from '@utils/i18n';
import { useIsPhone } from '@hooks/useIsPhone';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { Label } from '@components/ui/form/label';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { DrawerContent, DrawerHeader, DrawerRoot, DrawerTitle } from '@components/ui/drawer';
import { getFillDates, getMonthWeeks, getRangeKeys, latestDay, toKey } from './helpers';
import { TaskDaysPickerProps } from './types';

const MAX_TOTAL = 365;

export const TaskDaysPicker = ({
  name,
  days,
  total,
  trigger,
  open,
  setOpen,
  onJump,
  onApply,
  applying,
}: TaskDaysPickerProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'scheduling.daysPicker' });
  const { t: tActions } = useTranslation('app', { keyPrefix: 'actions' });
  const isPhone = useIsPhone();
  const existing = useMemo(() => new Map(days.map((day) => [day.key, day])), [days]);
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [picks, setPicks] = useState<string[]>([]);
  const [totalDays, setTotalDays] = useState(total);
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [drag, setDrag] = useState<{ from: string; to: string; adding: boolean } | null>(null);
  const draggedRef = useRef(false);

  const minimum = days.length + picks.length;
  const shownTotal = Math.max(totalDays, minimum);
  const remaining = shownTotal - minimum;
  const dirty = picks.length > 0 || shownTotal !== total;

  useEffect(() => {
    if (!open) return;
    const upcoming = days.find((day) => day.day >= new Date()) || days.at(-1);
    setMonth(startOfMonth(upcoming?.day || new Date()));
    setPicks([]);
    setTotalDays(total);
    setSkipWeekends(true);
  }, [open, days, total]);

  const close = () => setOpen(false);
  const handleOpenChange = (value: boolean) => {
    if (!value && dirty && isPhone) return;
    setOpen(value);
  };

  const dragKeys = drag
    ? getRangeKeys(drag.from, drag.to, drag.adding && skipWeekends).filter(
        (key) => !existing.has(key) && (drag.adding || picks.includes(key)),
      )
    : [];

  const keyAt = (x: number, y: number) =>
    (document.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>('[data-key]')
      ?.dataset.key;

  const startDrag = (e: PointerEvent<HTMLDivElement>) => {
    const key = (e.target as HTMLElement).closest<HTMLElement>('[data-key]')?.dataset.key;
    if (!key || existing.has(key) || (e.pointerType === 'mouse' && e.button !== 0)) return;
    draggedRef.current = false;
    setDrag({ from: key, to: key, adding: !picks.includes(key) });
  };

  const moveDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const key = keyAt(e.clientX, e.clientY);
    if (!key || key === drag.to) return;
    if (!e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.setPointerCapture(e.pointerId);
    draggedRef.current = true;
    setDrag({ ...drag, to: key });
  };

  const endDrag = () => {
    if (drag && draggedRef.current) {
      setPicks((prev) =>
        drag.adding
          ? [...new Set([...prev, ...dragKeys])]
          : prev.filter((key) => !dragKeys.includes(key)),
      );
    }
    setDrag(null);
  };

  const togglePick = (key: string) => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    const existingDay = existing.get(key);
    if (existingDay) {
      onJump(key);
      close();
      return;
    }
    setPicks((prev) => (prev.includes(key) ? prev.filter((el) => el !== key) : [...prev, key]));
  };

  const fillRemaining = () => {
    const taken = new Set([...existing.keys(), ...picks]);
    const after = latestDay([...taken], new Date(Date.now() - 86400000));
    setPicks((prev) => [
      ...prev,
      ...getFillDates({ count: remaining, taken, after, skipWeekends }),
    ]);
  };

  const weeks = getMonthWeeks(month);

  const body = (
    <div className="flex min-h-0 flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-neutral-800">{t('totalDays')}</span>
          <span className="text-xs tabular-nums text-neutral-500">
            {t('scheduled', { count: days.length + picks.length })}
            {remaining > 0 && (
              <b className="text-warning-700"> · {t('unscheduled', { count: remaining })}</b>
            )}
          </span>
        </div>
        <div className="flex items-center overflow-hidden rounded-lg border border-neutral-300">
          <button
            type="button"
            aria-label={t('fewer')}
            disabled={shownTotal <= minimum}
            onClick={() => setTotalDays(shownTotal - 1)}
            className="h-9 w-9 text-lg text-neutral-800 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:text-neutral-300 max-[899px]:h-11 max-[899px]:w-11">
            −
          </button>
          <span className="flex h-9 w-12 items-center justify-center border-x border-neutral-100 font-bold tabular-nums max-[899px]:h-11">
            {shownTotal}
          </span>
          <button
            type="button"
            aria-label={t('more')}
            disabled={shownTotal >= MAX_TOTAL}
            onClick={() => setTotalDays(shownTotal + 1)}
            className="h-9 w-9 text-lg text-neutral-800 hover:bg-neutral-50 disabled:text-neutral-300 max-[899px]:h-11 max-[899px]:w-11">
            +
          </button>
        </div>
      </div>
      <div className="overflow-y-auto overscroll-contain px-3 py-3">
        <div className="mb-1.5 flex items-center justify-between font-bold text-neutral-800">
          <button
            type="button"
            aria-label={t('previousMonth')}
            onClick={() => setMonth(addMonths(month, -1))}
            className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-neutral-50 max-[899px]:h-11 max-[899px]:w-11">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span>
            {longMonth(month)} {format(month, 'yyyy')}
          </span>
          <button
            type="button"
            aria-label={t('nextMonth')}
            onClick={() => setMonth(addMonths(month, 1))}
            className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-neutral-50 max-[899px]:h-11 max-[899px]:w-11">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <div
          className="grid touch-none select-none grid-cols-7 gap-0.5"
          role="grid"
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={() => setDrag(null)}>
          {weeks[0].map((day) => (
            <span
              key={`dow-${toKey(day)}`}
              className="py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
              {format(day, 'EEEEEE')}
            </span>
          ))}
          {weeks.flat().map((day) => {
            const key = toKey(day);
            if (!isSameMonth(day, month)) return <span key={key} />;
            const existingDay = existing.get(key);
            const inDrag = dragKeys.includes(key);
            const picked = drag && inDrag ? drag.adding : picks.includes(key);
            return (
              <button
                key={key}
                data-key={key}
                type="button"
                onClick={() => togglePick(key)}
                aria-pressed={Boolean(existingDay) || picked}
                aria-label={[
                  format(day, 'PPPP'),
                  existingDay ? t('scheduledDay') : picked ? t('newDay') : '',
                ]
                  .filter(Boolean)
                  .join(', ')}
                className={cn(
                  'relative h-[38px] rounded-md text-sm font-medium tabular-nums text-neutral-800 hover:bg-neutral-50 max-[899px]:h-11 max-[899px]:text-[15px]',
                  isWeekend(day) && 'text-neutral-400',
                  isToday(day) && 'shadow-[inset_0_0_0_1.5px_theme(colors.blue.500)]',
                  existingDay && 'bg-primary-500 font-bold text-white hover:bg-primary-600',
                  existingDay?.status === 'draft' &&
                    'border-2 border-dashed border-primary-500 bg-white text-primary-700 hover:bg-primary-50',
                  picked &&
                    'bg-primary-100 font-bold text-primary-700 ring-2 ring-inset ring-primary-500 hover:bg-primary-100',
                  drag && inDrag && !drag.adding && 'text-danger-600 line-through',
                )}>
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs leading-relaxed text-neutral-500">{t('hint')}</p>
        <div className="mt-2 flex items-center gap-2.5">
          <Checkbox
            id="days-picker-skip-weekends"
            checked={skipWeekends}
            onCheckedChange={(value) => setSkipWeekends(Boolean(value))}
          />
          <Label htmlFor="days-picker-skip-weekends">{t('skipWeekends')}</Label>
        </div>
        {remaining > 0 && (
          <Button
            type="button"
            variant="tertiary"
            disabled={false}
            onClick={fillRemaining}
            className="mt-2 h-10 w-full justify-center border-dashed max-[899px]:h-11">
            {t('fill', { count: remaining })}
          </Button>
        )}
        <p className="mt-2 text-xs text-neutral-500">{t('draftNote')}</p>
      </div>
      <div className="flex justify-end gap-2 border-t border-neutral-100 px-4 py-3 max-[899px]:pb-[calc(12px+env(safe-area-inset-bottom,0px))] max-[899px]:[&>button]:h-11 max-[899px]:[&>button]:flex-1">
        <Button type="button" variant="ghost" disabled={false} onClick={close}>
          {tActions('cancel')}
        </Button>
        <Button
          type="button"
          disabled={!dirty || applying}
          onClick={() => onApply({ dates: [...picks].sort(), totalDays: shownTotal })}>
          {t('apply')}
        </Button>
      </div>
    </div>
  );

  if (isPhone)
    return (
      <>
        {trigger}
        <DrawerRoot
          open={open}
          onOpenChange={handleOpenChange}
          direction="bottom"
          dismissible={!dirty}>
          <DrawerContent
            overlayClassName="z-[60] bg-[rgba(24,28,33,.45)]"
            className="max-h-[calc(100dvh-12px)] rounded-t-2xl border-0 shadow-[0_-8px_30px_rgba(0,0,0,.18)]">
            <DrawerHeader className="flex flex-row items-center justify-between border-b py-1.5 pl-4 pr-2">
              <DrawerTitle className="m-0 text-base font-bold text-neutral-800">
                {t('title', { name })}
              </DrawerTitle>
              <button
                type="button"
                aria-label={tActions('close')}
                onClick={close}
                className="flex h-11 w-11 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-50">
                <X className="h-5 w-5" />
              </button>
            </DrawerHeader>
            {body}
          </DrawerContent>
        </DrawerRoot>
      </>
    );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-0">
        <div className="flex items-center justify-between border-b border-neutral-100 py-2 pl-4 pr-2">
          <h3 className="text-[15px] font-bold text-neutral-800">{t('title', { name })}</h3>
          <button
            type="button"
            aria-label={tActions('close')}
            onClick={close}
            className="flex h-9 w-9 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-50">
            <X className="h-5 w-5" />
          </button>
        </div>
        {body}
      </PopoverContent>
    </Popover>
  );
};
